import React, { useState, useRef, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import "./VAContracts.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_c45d5dc5-1aa9-4fda-8179-43802ae25660/artifacts/r7bevoxt_1210928841187418.jpg";

const TIERS = [
  { v: "bank", label: "Commercial Bank" },
  { v: "credit_union", label: "Credit Union" },
  { v: "reit", label: "REIT" },
  { v: "receiver", label: "Court-Appointed Receiver" },
  { v: "special_servicer", label: "Special Servicer" },
  { v: "default_servicing", label: "Default Servicing" },
  { v: "family_office", label: "Family Office / RIA" },
  { v: "insurance_carrier", label: "Insurance Carrier" },
  { v: "private_equity", label: "Private Equity" },
  { v: "asset_manager", label: "Asset Manager" },
  { v: "law_firm", label: "Law Firm / Counsel" },
  { v: "other", label: "Other" },
];

const PACKETS = [
  { v: "underwriter-packet", label: "Bank Underwriter Packet (full vendor file + MVSA template)" },
  { v: "banker-briefing", label: "Banker Briefing (2-page institutional summary)" },
];

const NDA_PREVIEW = `UNIVERSAL RECIPIENT NON-DISCLOSURE AGREEMENT
(One-Way; Moovin Forward LLC as Disclosing Party)

By signing below, Recipient and the entity Recipient represents:

1. PARTIES. Moovin Forward LLC (Disclosing) and the Recipient/Entity identified below.

2. CONFIDENTIAL INFORMATION. The Banker Briefing, Bank Underwriter Packet, MVSA template, Labor Matrix, Material Cost Cap formula, Custodial Continuity Protocol, Site Access Gate Protocol, palette systems, pricing model, regulatory architecture, photographs, methodologies, processes, know-how, and all derivatives in any format.

3. TRADE SECRET STATUS. Materials are trade secrets protected under TUTSA (Tex. Civ. Prac. & Rem. Code Ch. 134A) and the federal Defend Trade Secrets Act (18 U.S.C. § 1836).

4. RECIPIENT OBLIGATIONS. Recipient shall: (a) hold in strict confidence; (b) NOT reproduce, photograph, screenshot, scan, transcribe, summarize, distribute, post, publish, or disclose; (c) NOT use for any purpose other than evaluating a potential engagement with Moovin Forward; (d) NOT mimic, replicate, reverse-engineer, or build any competing process based on the Materials; (e) limit access to need-to-know personnel bound by equivalent confidentiality; (f) safeguard with commercially reasonable care.

5. NO LICENSE. No IP rights transferred. Temporary review right only.

6. PERMITTED DISCLOSURES. Recipient's own counsel/auditors/officers under equivalent NDA; compelled legal process with notice; Moovin Forward's prior written consent.

7. TERM. Three (3) years from acknowledgment. Trade-secret protections survive indefinitely while Materials remain secret.

8. RETURN OR DESTRUCTION. Within 10 business days of written request, return or destroy and certify in writing.

9. EQUITABLE RELIEF. Breach causes irreparable harm. Moovin Forward entitled to injunctive relief without bond, plus damages, exemplary damages, and attorneys' fees.

10. AUTHORITY. Signer warrants actual authority to bind the entity.

11. GOVERNING LAW; VENUE. Texas law. Exclusive venue: Tarrant County, Texas.

12. ELECTRONIC SIGNATURE. Typed-name + checkbox constitutes valid signature under E-SIGN (15 U.S.C. § 7001) and Texas UETA (Tex. Bus. & Com. Code Ch. 322).

13. ENTIRE AGREEMENT. Recipient's own NDA may be substituted by mutual written agreement; this acknowledgment is the minimum baseline binding upon delivery.

[End of NDA preview — full text included on cover page of delivered PDF.]`;

export default function RequestPacket() {
  const [searchParams] = useSearchParams();
  const presetPacket = searchParams.get("packet") || "underwriter-packet";

  const [mode, setMode] = useState("universal"); // 'universal' | 'alt'
  const [altSubmitted, setAltSubmitted] = useState(false);
  const [altForm, setAltForm] = useState({
    legal_name: "",
    email: "",
    entity: "",
    role: "",
    tier: "bank",
    packet: PACKETS.find(p => p.v === presetPacket) ? presetPacket : "underwriter-packet",
    entity_nda_title: "",
    entity_nda_date: "",
    placement_context: "",
    nda_file_base64: "",
    nda_file_name: "",
  });
  const [altStatus, setAltStatus] = useState("idle");
  const [altErr, setAltErr] = useState("");

  const handleAltChange = (e) => {
    const { name, value } = e.target;
    setAltForm(f => ({ ...f, [name]: value }));
  };

  const handleAltFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      setAltErr("Please attach a PDF.");
      setAltStatus("error");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setAltErr("File exceeds 3 MB limit.");
      setAltStatus("error");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = (reader.result || "").toString().split(",")[1] || "";
      setAltForm(f => ({ ...f, nda_file_base64: b64, nda_file_name: file.name }));
      setAltErr("");
      setAltStatus("idle");
    };
    reader.readAsDataURL(file);
  };

  const handleAltSubmit = async (e) => {
    e.preventDefault();
    setAltErr("");
    if (!altForm.legal_name.trim() || !altForm.email.includes("@")) {
      setAltErr("Legal name and a valid work email are required."); setAltStatus("error"); return;
    }
    if (!altForm.entity.trim()) {
      setAltErr("Entity legal name is required."); setAltStatus("error"); return;
    }
    if (!altForm.nda_file_base64) {
      setAltErr("Please attach your institution's NDA (PDF)."); setAltStatus("error"); return;
    }
    setAltStatus("sending");
    try {
      const r = await fetch(`${BACKEND_URL}/api/packet-request/alt-nda`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(altForm),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Submission failed.");
      setAltSubmitted(true);
      setAltStatus("sent");
    } catch (err) {
      setAltErr(err.message || "Submission failed.");
      setAltStatus("error");
    }
  };

  const [form, setForm] = useState({
    legal_name: "",
    email: "",
    entity: "",
    role: "",
    tier: "bank",
    packet: PACKETS.find(p => p.v === presetPacket) ? presetPacket : "underwriter-packet",
    typed_signature: "",
    authority_acknowledged: false,
    scrolled_to_bottom: false,
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errMsg, setErrMsg] = useState("");
  const ndaRef = useRef(null);

  // Scroll-to-bottom detection
  useEffect(() => {
    const el = ndaRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
        setForm(f => f.scrolled_to_bottom ? f : { ...f, scrolled_to_bottom: true });
      }
    };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    // Client-side guards mirror server
    if (!form.legal_name.trim() || !form.email.includes("@")) {
      setErrMsg("Legal name and a valid work email are required."); setStatus("error"); return;
    }
    if (!form.entity.trim()) {
      setErrMsg("Entity legal name is required."); setStatus("error"); return;
    }
    if (!form.scrolled_to_bottom) {
      setErrMsg("Please scroll to the bottom of the NDA before signing."); setStatus("error"); return;
    }
    if (form.typed_signature.trim().toLowerCase() !== form.legal_name.trim().toLowerCase()) {
      setErrMsg("Typed signature must match your legal name exactly."); setStatus("error"); return;
    }
    if (!form.authority_acknowledged) {
      setErrMsg("Please confirm you have authority to bind your entity."); setStatus("error"); return;
    }
    setStatus("sending");
    try {
      const r = await fetch(`${BACKEND_URL}/api/packet-request/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Submission failed.");
      // Trigger download in a new tab
      window.location.href = `${BACKEND_URL}/api/packet-request/${d.download_token}/download`;
      setStatus("sent");
    } catch (err) {
      setErrMsg(err.message || "Submission failed.");
      setStatus("error");
    }
  };

  const packetLabel = PACKETS.find(p => p.v === form.packet)?.label || "Packet";

  return (
    <div className="vac-page" data-testid="request-packet-page">
      <nav className="vac-nav">
        <Link to="/" className="vac-nav-logo"><img src={LOGO_URL} alt="Moovin Forward" /></Link>
        <Link to="/" className="vac-nav-back" data-testid="rp-back-home">← Home</Link>
      </nav>

      <header className="vac-hero">
        <div className="vac-hero-band" aria-hidden="true" />
        <div className="vac-hero-inner">
          <p className="vac-eyebrow">🛡️ TUTSA · DTSA · TX-Law NDA</p>
          <h1 className="vac-h1">Request Institutional Packet<br />
            <span className="vac-h1-accent">Under Universal NDA</span>
          </h1>
          <p className="vac-lede" data-testid="rp-hero-lede">
            Moovin Forward's institutional methodology is protected as trade-secret under
            Texas and federal law. Sign the one-page NDA below, and the requested packet
            will download immediately — stamped with your name and entity on its cover page.
          </p>
        </div>
      </header>

      {status === "sent" ? (
        <section className="vac-section" data-testid="rp-sent-section">
          <div className="vac-section-inner" style={{ maxWidth: 720, textAlign: "center" }}>
            <h2 className="vac-h2">Acknowledged. Download starting.</h2>
            <p className="vac-body" style={{ margin: "1.5rem auto" }}>
              Your acknowledgment is on file. The packet is downloading in a new tab.
              If it didn't start, your link is single-use and good for 15 minutes — return
              to <Link to="/request-packet">/request-packet</Link> to issue a fresh one.
            </p>
            <p className="vac-body" style={{ color: "#6b7280", fontSize: "0.9rem" }}>
              For substantive questions: <strong>Shannon A. Ware</strong> — (817) 381-8831 — hello@moovinforward.org
            </p>
          </div>
        </section>
      ) : (
        <section className="vac-section" data-testid="rp-form-section">
          <div className="vac-section-inner" style={{ maxWidth: 880 }}>

            {/* Mode toggle: Universal NDA vs Bring Your Own NDA */}
            <div
              data-testid="rp-mode-toggle"
              style={{
                display: "flex", gap: "0.6rem", marginBottom: "1.6rem",
                background: "#fdfcf6", border: "1px solid #d8d2bf",
                padding: "0.45rem", borderRadius: 4, flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => setMode("universal")}
                data-testid="rp-mode-universal"
                style={{
                  flex: "1 1 200px",
                  padding: "0.75rem 1rem",
                  background: mode === "universal" ? "#1b2a4e" : "transparent",
                  color: mode === "universal" ? "#f5efe1" : "#1b2a4e",
                  border: "none", borderRadius: 3,
                  fontWeight: 700, fontSize: "0.88rem", letterSpacing: "0.02em",
                  cursor: "pointer", fontFamily: "Helvetica Neue, Arial, sans-serif",
                  transition: "background 0.2s ease, color 0.2s ease",
                }}
              >
                🛡️ Sign Moovin Forward's NDA
              </button>
              <button
                type="button"
                onClick={() => setMode("alt")}
                data-testid="rp-mode-alt"
                style={{
                  flex: "1 1 200px",
                  padding: "0.75rem 1rem",
                  background: mode === "alt" ? "#7a8f7e" : "transparent",
                  color: mode === "alt" ? "#f5efe1" : "#5e7263",
                  border: "none", borderRadius: 3,
                  fontWeight: 700, fontSize: "0.88rem", letterSpacing: "0.02em",
                  cursor: "pointer", fontFamily: "Helvetica Neue, Arial, sans-serif",
                  transition: "background 0.2s ease, color 0.2s ease",
                }}
              >
                📥 Use My Institution's NDA
              </button>
            </div>

            {mode === "alt" ? (
              altSubmitted ? (
                <div className="vac-form vac-form-sent" data-testid="rp-alt-sent">
                  <h3 className="vac-form-thanks">Your NDA is in Shannon's queue.</h3>
                  <p>She will personally review your institution's NDA and respond within one business day. On approval, you'll receive a single-use download link via email — the packet will be delivered under your NDA, not ours.</p>
                  <p className="vac-form-thanks-meta">For substantive questions: (817) 381-8831 · hello@moovinforward.org</p>
                </div>
              ) : (
                <form onSubmit={handleAltSubmit} className="vac-form" data-testid="rp-alt-form" style={{ gap: "1.2rem" }}>
                  <h2 className="vac-h2" style={{ marginBottom: 0 }}>Bring Your Own NDA</h2>
                  <p className="vac-body" style={{ margin: 0 }}>
                    Upload your institution's NDA below. Shannon will personally review and respond within one business day. On approval, the packet releases under <strong>your</strong> NDA — not ours.
                  </p>

                  <div className="vac-field-row">
                    <label className="vac-field">
                      <span>Legal Name <em>*</em></span>
                      <input type="text" name="legal_name" value={altForm.legal_name} onChange={handleAltChange} required data-testid="rp-alt-name" />
                    </label>
                    <label className="vac-field">
                      <span>Work Email <em>*</em></span>
                      <input type="email" name="email" value={altForm.email} onChange={handleAltChange} required data-testid="rp-alt-email" />
                    </label>
                  </div>

                  <div className="vac-field-row">
                    <label className="vac-field">
                      <span>Entity Legal Name <em>*</em></span>
                      <input type="text" name="entity" value={altForm.entity} onChange={handleAltChange} required data-testid="rp-alt-entity" />
                    </label>
                    <label className="vac-field">
                      <span>Role / Title</span>
                      <input type="text" name="role" value={altForm.role} onChange={handleAltChange} data-testid="rp-alt-role" />
                    </label>
                  </div>

                  <div className="vac-field-row">
                    <label className="vac-field">
                      <span>Entity Tier</span>
                      <select name="tier" value={altForm.tier} onChange={handleAltChange} data-testid="rp-alt-tier"
                              style={{ width: "100%", padding: "0.75rem 0.9rem", border: "1px solid #d8d2bf", borderRadius: 2, background: "#fdfcf6", fontFamily: "Helvetica Neue, Arial, sans-serif", fontSize: "0.98rem" }}>
                        {TIERS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                      </select>
                    </label>
                    <label className="vac-field">
                      <span>Packet Requested</span>
                      <select name="packet" value={altForm.packet} onChange={handleAltChange} data-testid="rp-alt-packet"
                              style={{ width: "100%", padding: "0.75rem 0.9rem", border: "1px solid #d8d2bf", borderRadius: 2, background: "#fdfcf6", fontFamily: "Helvetica Neue, Arial, sans-serif", fontSize: "0.98rem" }}>
                        {PACKETS.map(p => <option key={p.v} value={p.v}>{p.label}</option>)}
                      </select>
                    </label>
                  </div>

                  <div className="vac-field-row">
                    <label className="vac-field">
                      <span>NDA Title (optional)</span>
                      <input type="text" name="entity_nda_title" value={altForm.entity_nda_title} onChange={handleAltChange}
                             placeholder="e.g. Mutual Confidentiality Agreement" data-testid="rp-alt-nda-title" />
                    </label>
                    <label className="vac-field">
                      <span>NDA Date (optional)</span>
                      <input type="text" name="entity_nda_date" value={altForm.entity_nda_date} onChange={handleAltChange}
                             placeholder="e.g. 2026-02-19" data-testid="rp-alt-nda-date" />
                    </label>
                  </div>

                  <label className="vac-field">
                    <span>Attach Your NDA (PDF, max 3 MB) <em>*</em></span>
                    <input type="file" accept="application/pdf" onChange={handleAltFile} data-testid="rp-alt-file"
                           style={{ padding: "0.6rem", background: "#fdfcf6", border: "1px solid #d8d2bf", borderRadius: 2 }} />
                    {altForm.nda_file_name && (
                      <small style={{ color: "#5e7263", fontSize: "0.78rem", marginTop: 4 }}>
                        ✓ Attached: {altForm.nda_file_name}
                      </small>
                    )}
                  </label>

                  <label className="vac-field">
                    <span>Context (optional)</span>
                    <textarea name="placement_context" value={altForm.placement_context} onChange={handleAltChange} rows={3}
                              placeholder="Any specific terms or context Shannon should know about." data-testid="rp-alt-context" />
                  </label>

                  {altStatus === "error" && (
                    <div className="vac-form-error" data-testid="rp-alt-error">{altErr}</div>
                  )}

                  <button type="submit" className="vac-btn vac-btn-primary vac-btn-full"
                          disabled={altStatus === "sending"} data-testid="rp-alt-submit">
                    {altStatus === "sending" ? "Submitting…" : "📥 Submit NDA for Review"}
                  </button>

                  <p className="vac-form-fine">
                    Packet is held until Shannon personally reviews and approves your NDA. Response within 1 business day.
                  </p>
                </form>
              )
            ) : (

            <form onSubmit={handleSubmit} className="vac-form" style={{ gap: "1.4rem" }}>
              <h2 className="vac-h2" style={{ marginBottom: 0 }}>Recipient Information</h2>

              <div className="vac-field-row">
                <label className="vac-field">
                  <span>Legal Name <em>*</em></span>
                  <input type="text" name="legal_name" value={form.legal_name} onChange={handleChange} required data-testid="rp-legal-name" />
                </label>
                <label className="vac-field">
                  <span>Work Email <em>*</em></span>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required data-testid="rp-email" />
                </label>
              </div>

              <div className="vac-field-row">
                <label className="vac-field">
                  <span>Entity Legal Name <em>*</em></span>
                  <input type="text" name="entity" value={form.entity} onChange={handleChange} required
                         placeholder="e.g. First Republic Bank, NA" data-testid="rp-entity" />
                </label>
                <label className="vac-field">
                  <span>Role / Title</span>
                  <input type="text" name="role" value={form.role} onChange={handleChange}
                         placeholder="e.g. SVP Vendor Management" data-testid="rp-role" />
                </label>
              </div>

              <div className="vac-field-row">
                <label className="vac-field">
                  <span>Entity Tier</span>
                  <select name="tier" value={form.tier} onChange={handleChange} data-testid="rp-tier"
                          style={{ width: "100%", padding: "0.75rem 0.9rem", border: "1px solid #d8d2bf", borderRadius: 2, background: "#fdfcf6", fontFamily: "Helvetica Neue, Arial, sans-serif", fontSize: "0.98rem" }}>
                    {TIERS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                  </select>
                </label>
                <label className="vac-field">
                  <span>Packet Requested</span>
                  <select name="packet" value={form.packet} onChange={handleChange} data-testid="rp-packet"
                          style={{ width: "100%", padding: "0.75rem 0.9rem", border: "1px solid #d8d2bf", borderRadius: 2, background: "#fdfcf6", fontFamily: "Helvetica Neue, Arial, sans-serif", fontSize: "0.98rem" }}>
                    {PACKETS.map(p => <option key={p.v} value={p.v}>{p.label}</option>)}
                  </select>
                </label>
              </div>

              <h2 className="vac-h2" style={{ marginTop: "1rem", marginBottom: 0 }}>Universal NDA</h2>
              <p className="vac-body" style={{ margin: 0 }}>
                Scroll through the full NDA below. The complete signed text travels on the cover page of the PDF you'll receive.
              </p>

              <div
                ref={ndaRef}
                data-testid="rp-nda-scroll"
                style={{
                  maxHeight: 320,
                  overflowY: "auto",
                  border: form.scrolled_to_bottom ? "2px solid #5e7263" : "2px solid #d8d2bf",
                  background: "#fdfcf6",
                  padding: "1.2rem 1.4rem",
                  fontFamily: "Helvetica Neue, Arial, sans-serif",
                  fontSize: "0.82rem",
                  lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                  color: "#2a2a2a",
                  transition: "border 0.25s ease",
                }}
              >
                {NDA_PREVIEW}
              </div>
              <div style={{ fontSize: "0.78rem", color: form.scrolled_to_bottom ? "#5e7263" : "#9b8a55", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                {form.scrolled_to_bottom
                  ? "✓ NDA reviewed in full"
                  : "↓ Please scroll to the bottom of the NDA"}
              </div>

              <label className="vac-field" style={{ marginTop: "0.4rem" }}>
                <span>Typed-Name Signature <em>*</em></span>
                <input
                  type="text" name="typed_signature" value={form.typed_signature} onChange={handleChange} required
                  placeholder="Type your full legal name exactly as entered above"
                  data-testid="rp-typed-signature"
                />
                <small style={{ fontSize: "0.74rem", color: "#6b7280", marginTop: 4 }}>
                  Must match your Legal Name above exactly. Electronic signature per E-SIGN Act + Texas UETA.
                </small>
              </label>

              <label style={{ display: "flex", gap: "0.7rem", alignItems: "flex-start", fontSize: "0.88rem", color: "#3a3a3a", cursor: "pointer" }}>
                <input
                  type="checkbox" name="authority_acknowledged"
                  checked={form.authority_acknowledged} onChange={handleChange}
                  data-testid="rp-authority"
                  style={{ marginTop: 4, width: 18, height: 18, accentColor: "#1b2a4e" }}
                />
                <span>
                  I represent and warrant that I hold actual authority to bind <strong>{form.entity || "the named entity"}</strong> to this NDA, and I have read and accept all terms.
                </span>
              </label>

              {status === "error" && (
                <div className="vac-form-error" data-testid="rp-error">{errMsg}</div>
              )}

              <button
                type="submit" className="vac-btn vac-btn-primary vac-btn-full"
                disabled={status === "sending"} data-testid="rp-submit"
              >
                {status === "sending" ? "Acknowledging…" : `🛡️ Sign NDA & Download ${packetLabel.split(" (")[0]}`}
              </button>

              <p className="vac-form-fine">
                Acknowledgment is logged with timestamp, IP, and NDA SHA-256 for audit defense.
                Bound for three (3) years from today; trade-secret protections survive indefinitely.
              </p>
            </form>
            )}
          </div>
        </section>
      )}

      <footer className="vac-footer" data-testid="rp-footer">
        <div className="vac-footer-inner">
          <div><strong>Moovin Forward LLC</strong> — Interior Unit Stabilization · DFW Metroplex</div>
          <div className="vac-footer-disclaim">
            CONFIDENTIAL &amp; PROPRIETARY — Protected under Texas Uniform Trade Secrets Act (TUTSA) and the federal Defend Trade Secrets Act (DTSA).
            <strong> No brokerage. No property management. No leasing.</strong>
          </div>
          <div className="vac-footer-links">
            <Link to="/">Home</Link>
            <Link to="/va-contracts">VA Contracts</Link>
            <Link to="/privacy">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
